import { renderAuthRequiredDialogContent, renderDialogContent } from './render-dialog';
import type { DialogApi, MapPageDom } from './types';
import { DialogFocusRestorer } from './dialog-focus';

/** Duration, in milliseconds, shared by the dialog closing transition and deferred content cleanup. */
const DIALOG_TRANSITION_DURATION_MS = 200;
/** Custom event emitted before the site login dialog replaces an open map dialog. */
const SITE_AUTH_BEFORE_OPEN_LOGIN_EVENT = 'site-auth:before-open-login';

/**
 * Initializes the reusable map dialog controller, including scroll locking, focus trapping and exact opener restoration.
 *
 * @param params DOM references for the map screen.
 * @returns Dialog controller facade shared by pointer and keyboard activation flows.
 */
export function initializeDialog(params: { mapDom: MapPageDom }): DialogApi {
  const { dialogRoot, dialogContent, pageBody } = params.mapDom;
  let previousBodyOverflow = '';
  let closeTimeoutId: number | null = null;
  const focusRestorer = new DialogFocusRestorer();

  /**
   * Locks document scroll while the modal overlay is visible.
   *
   * @returns Nothing. Preserves the pre-dialog inline overflow value for later restoration.
   */
  const lockPageScroll = (): void => {
    if (pageBody.style.overflow === 'hidden') {
      return;
    }

    previousBodyOverflow = pageBody.style.overflow;
    pageBody.style.overflow = 'hidden';
  };

  /**
   * Restores document scroll state after modal close.
   *
   * @returns Nothing. Restores the inline overflow value captured by `lockPageScroll`.
   */
  const unlockPageScroll = (): void => {
    pageBody.style.overflow = previousBodyOverflow;
  };

  /**
   * Hides the overlay, restores page scroll and returns focus to the exact retained opener.
   * Every close mechanism delegates here, including Escape, backdrop, close, cancel and successful submit.
   *
   * @returns Nothing.
   */
  const closeDialog = (): void => {
    if (dialogRoot.getAttribute('aria-hidden') === 'true') {
      return;
    }

    unlockPageScroll();
    dialogRoot.classList.add('opacity-0', 'pointer-events-none');
    dialogRoot.classList.remove('opacity-100', 'pointer-events-auto');
    const panelElement = dialogContent.firstElementChild;
    if (panelElement instanceof HTMLElement) {
      panelElement.classList.add('scale-95', 'opacity-0');
      panelElement.classList.remove('scale-100', 'opacity-100');
    }
    dialogRoot.setAttribute('aria-hidden', 'true');
    focusRestorer.restore();

    if (closeTimeoutId !== null) {
      window.clearTimeout(closeTimeoutId);
    }

    closeTimeoutId = window.setTimeout(() => {
      dialogContent.textContent = '';
      closeTimeoutId = null;
    }, DIALOG_TRANSITION_DURATION_MS);
  };

  dialogRoot.className =
    'fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/20 p-4 backdrop-blur-[1.0px] opacity-0 pointer-events-none transition duration-200';
  dialogRoot.setAttribute('aria-hidden', 'true');
  dialogContent.textContent = '';

  dialogRoot.addEventListener('click', (event) => {
    if (event.target === dialogRoot) {
      closeDialog();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (dialogRoot.getAttribute('aria-hidden') === 'true') {
      return;
    }

    if (event.key === 'Tab') {
      const focusableElements = Array.from(
        dialogContent.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (firstElement && lastElement) {
        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
      return;
    }

    if (event.key === 'Escape') {
      closeDialog();
    }
  });

  window.addEventListener(SITE_AUTH_BEFORE_OPEN_LOGIN_EVENT, () => {
    closeDialog();
  });

  return {
    open(openParams) {
      if (dialogRoot.getAttribute('aria-hidden') === 'false') {
        return;
      }

      if (closeTimeoutId !== null) {
        window.clearTimeout(closeTimeoutId);
        closeTimeoutId = null;
      }

      focusRestorer.remember(openParams.opener);

      renderDialogContent({
        dialogContent,
        fieldNumber: openParams.fieldNumber,
        dialogData: openParams.dialogData,
        closeDialog,
      });
      lockPageScroll();
      dialogRoot.setAttribute('aria-hidden', 'false');
      dialogRoot.classList.remove('opacity-0', 'pointer-events-none');
      dialogRoot.classList.add('opacity-100', 'pointer-events-auto');

      const panelElement = dialogContent.firstElementChild;
      if (panelElement instanceof HTMLElement) {
        window.requestAnimationFrame(() => {
          panelElement.classList.remove('scale-95', 'opacity-0');
          panelElement.classList.add('scale-100', 'opacity-100');
        });
      }
    },
    openAuthRequired() {
      if (dialogRoot.getAttribute('aria-hidden') === 'false') {
        return;
      }

      if (closeTimeoutId !== null) {
        window.clearTimeout(closeTimeoutId);
        closeTimeoutId = null;
      }

      const activeElement = document.activeElement;
      focusRestorer.remember(
        activeElement instanceof HTMLElement || activeElement instanceof SVGElement
          ? activeElement
          : null,
      );

      renderAuthRequiredDialogContent({
        dialogContent,
        closeDialog,
      });
      lockPageScroll();
      dialogRoot.setAttribute('aria-hidden', 'false');
      dialogRoot.classList.remove('opacity-0', 'pointer-events-none');
      dialogRoot.classList.add('opacity-100', 'pointer-events-auto');

      const panelElement = dialogContent.firstElementChild;
      if (panelElement instanceof HTMLElement) {
        window.requestAnimationFrame(() => {
          panelElement.classList.remove('scale-95', 'opacity-0');
          panelElement.classList.add('scale-100', 'opacity-100');
        });
      }
    },
    close() {
      closeDialog();
    },
    isOpen() {
      return dialogRoot.getAttribute('aria-hidden') === 'false';
    },
  };
}
